import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  AddressService,
  AddressValidationConfig,
  AddressValidationPayload,
  AddressValidationResult,
  StructuredAddress,
} from './address.service';
import { AddressValidationPromptComponent } from 'src/app/shared/components/smart/address-validation-prompt/address-validation-prompt.component';
import {
  AddressValidationPromptAddress,
  AddressValidationPromptData,
  AddressValidationPromptResult,
} from 'src/app/shared/components/smart/address-validation-prompt/address-validation-prompt.types';

export interface ProfileAddressValidationRequest {
  userId: string;
  primaryAddress: StructuredAddress;
  initialPrimaryAddressSnapshot: string;
  existingAddressValidation?: AddressValidationPayload | null;
}

export interface ProfileAddressValidationSaveResult {
  cancelled: boolean;
  primaryAddress: StructuredAddress;
  addressValidation?: AddressValidationPayload;
}

/**
 * CPK-4914: the profile address belongs to the user, not to a claim, so it is always
 * validated with this fixed rule instead of borrowing a case's `address_validation`
 * config. Default lambda criteria; insist once (maxRetries: 1) before the prompt offers
 * "confirm as is". AGENT/ADMINISTRATOR callers still have no retry ceiling server-side.
 */
export const PROFILE_ADDRESS_VALIDATION_CONFIG: AddressValidationConfig = {
  enabled: true,
  maxRetries: 1,
};

@Injectable({
  providedIn: 'root',
})
export class ProfileAddressValidationService {
  constructor(
    private addressService: AddressService,
    private dialog: MatDialog
  ) {}

  normalizedAddressSnapshot(address?: StructuredAddress | null): string {
    const normalized = this.addressFields().reduce((acc, key) => {
      acc[key] = String(address?.[key] ?? '').trim();
      return acc;
    }, {} as Record<string, string>);

    return JSON.stringify(normalized);
  }

  async resolveBeforeProfileSave(
    request: ProfileAddressValidationRequest
  ): Promise<ProfileAddressValidationSaveResult> {
    const primaryAddress = { ...request.primaryAddress };

    if (
      this.normalizedAddressSnapshot(primaryAddress) ===
      request.initialPrimaryAddressSnapshot
    ) {
      return { cancelled: false, primaryAddress };
    }

    const config = PROFILE_ADDRESS_VALIDATION_CONFIG;

    const validation = await this.addressService.validateAddress(
      request.userId,
      this.toValidationAddress(primaryAddress),
      config,
      request.existingAddressValidation?.response_id ?? null
    );

    if (this.canSaveWithAddressValidation(validation)) {
      return {
        cancelled: false,
        primaryAddress,
        addressValidation: validation.payload,
      };
    }

    return this.handleFailedAddressValidationPrompt(
      request.userId,
      primaryAddress,
      validation,
      config
    );
  }

  private async handleFailedAddressValidationPrompt(
    userId: string,
    enteredAddress: StructuredAddress,
    validation: AddressValidationResult,
    config: AddressValidationConfig
  ): Promise<ProfileAddressValidationSaveResult> {
    let currentAddress = { ...enteredAddress };
    let currentValidation = validation;

    while (true) {
      const promptResult = await this.openAddressValidationPrompt(
        currentAddress,
        currentValidation
      );

      if (!promptResult || promptResult.action === 'cancelled') {
        return { cancelled: true, primaryAddress: currentAddress };
      }

      if (promptResult.action === 'confirmed_override') {
        return {
          cancelled: false,
          primaryAddress: currentAddress,
          // CPK-4239: a human-confirmed override resolves the retry streak —
          // don't let it count against future, unrelated edits.
          addressValidation: {
            ...(promptResult.addressValidation ?? { status: 'user_confirmed', user_confirmed_override: true }),
            retry_count: 0,
          },
        };
      }

      if (promptResult.action === 'modified_address' && promptResult.address) {
        currentAddress = this.mergePromptAddress(
          currentAddress,
          promptResult.address
        );
        const revalidation = await this.addressService.validateAddress(
          userId,
          this.toValidationAddress(currentAddress),
          config,
          currentValidation.payload?.response_id ?? null
        );

        if (this.canSaveWithAddressValidation(revalidation)) {
          return {
            cancelled: false,
            primaryAddress: currentAddress,
            addressValidation: revalidation.payload,
          };
        }

        currentValidation = revalidation;
      }
    }
  }

  private openAddressValidationPrompt(
    enteredAddress: StructuredAddress,
    validation: AddressValidationResult
  ): Promise<AddressValidationPromptResult | undefined> {
    const data: AddressValidationPromptData = {
      enteredAddress: this.toPromptAddress(enteredAddress),
      validationPayload: validation.payload,
      previousResponseId: validation.payload?.response_id ?? null,
    };

    const ref = this.dialog.open<
      AddressValidationPromptComponent,
      AddressValidationPromptData,
      AddressValidationPromptResult
    >(AddressValidationPromptComponent, {
      data,
      autoFocus: false,
      disableClose: true,
      panelClass: 'address-validation-prompt-dialog',
    });

    return firstValueFrom(ref.afterClosed());
  }

  private canSaveWithAddressValidation(result: AddressValidationResult): boolean {
    if (result.payload?.llm_suspicion?.suspicious === true) return false;
    if (result.decision?.status === 'passed') {
      return !result.payload?.flagged;
    }
    return result.decision?.status === 'skipped';
  }

  private addressFields(): Array<keyof StructuredAddress> {
    return [
      'address_line_1',
      'streetNumber',
      'streetName',
      'city',
      'neighborhood',
      'state',
      'county',
      'country',
      'postcode',
      'postalCode',
      'buildingName',
      'unitNumber',
    ];
  }

  private toValidationAddress(address: StructuredAddress): StructuredAddress {
    return {
      ...address,
      streetName: address.streetName || address.address_line_1 || '',
      postalCode: address.postalCode || address.postcode || '',
    };
  }

  private toPromptAddress(
    address: StructuredAddress
  ): AddressValidationPromptAddress {
    return {
      addressLine1: address.address_line_1 ?? null,
      streetNumber: address.streetNumber ?? null,
      streetName: address.streetName ?? address.address_line_1 ?? null,
      city: address.city ?? null,
      neighborhood: address.neighborhood ?? null,
      state: address.state ?? null,
      county: address.county ?? null,
      country: address.country ?? null,
      postcode: address.postcode ?? null,
      postalCode: address.postalCode ?? address.postcode ?? null,
      buildingName: address.buildingName ?? null,
      unitNumber: address.unitNumber ?? null,
    };
  }

  private mergePromptAddress(
    currentAddress: StructuredAddress,
    promptAddress: AddressValidationPromptAddress
  ): StructuredAddress {
    return {
      ...currentAddress,
      address_line_1: '',
      streetNumber: promptAddress.streetNumber ?? '',
      streetName: promptAddress.streetName ?? '',
      city: promptAddress.city ?? '',
      neighborhood: promptAddress.neighborhood ?? '',
      state: promptAddress.state ?? '',
      county: promptAddress.county ?? '',
      country: promptAddress.country ?? '',
      postcode: promptAddress.postcode ?? promptAddress.postalCode ?? '',
      postalCode: promptAddress.postalCode ?? promptAddress.postcode ?? '',
      buildingName: promptAddress.buildingName ?? '',
      unitNumber: promptAddress.unitNumber ?? '',
      // CPK-4239 fix: this explicit-field rebuild was dropping resolvedVia (the modal
      // sets it in getEditedAddress(), but it never survived this conversion), so a
      // correction made here always persisted with no resolvedVia at all.
      resolvedVia: promptAddress.resolvedVia ?? 'manual',
    };
  }
}
